import { useMemo, useState } from 'react';
import { useIsFetching, useQuery } from '@tanstack/react-query';
import { FolderKanban } from 'lucide-react';
import { EmptyState, ErrorState } from '@/pages/Dashboard';
import { getDomains, getProjects } from '@/services/api';
import { getNavigationEntries } from '@/services/api/navigation-report';
import { EntriesCards } from './components/EntriesCards';
import { EntriesTable } from './components/EntriesTable';
import { EntryDetail } from './components/EntryDetail';
import { Filters } from './components/Filters';
import { NAV_QUERY_KEYS, readStoredEntry, storeEntry, useNavigationFilters } from './hooks/useNavigationFilters';

const LIST_LIMIT = 500;

export default function NavigationReport() {
    const filters = useNavigationFilters();
    // Página de entrada aberta (estado 2). Nada na URL; a última aberta fica no localStorage.
    const [entryPath, setEntryPathState] = useState<string | null>(() => readStoredEntry());
    const setEntryPath = (path: string | null) => {
        setEntryPathState(path);
        storeEntry(path);
    };

    const [isOwner] = useState<boolean>(() => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            return user?.owner === true || user?.owner === 1;
        } catch {
            return false;
        }
    });

    const { data: projects } = useQuery({ queryKey: ['projects'], queryFn: getProjects });
    const hasProject = filters.projectIds.length > 0;
    const { data: domains } = useQuery({
        queryKey: ['domains', filters.projectIds],
        queryFn: () => getDomains(filters.projectIds),
        enabled: hasProject,
    });

    // Trocar de projeto muda o contexto inteiro: fecha a entrada aberta (o hook já limpa os domínios).
    const filtersForBar = useMemo(
        () => ({
            ...filters,
            setProjectIds: (ids: number[]) => {
                filters.setProjectIds(ids);
                setEntryPath(null);
            },
        }),
        [filters]
    );

    const request = useMemo(() => ({ ...filters.request, limit: LIST_LIMIT }), [filters.request]);
    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey: ['navigation_entries', request],
        queryFn: () => getNavigationEntries(request),
        enabled: hasProject && !entryPath,
        retry: (count, err: any) => count < 1 && ![403, 422].includes(err?.response?.status),
    });

    const busy = useIsFetching({ predicate: (q) => NAV_QUERY_KEYS.includes(String(q.queryKey[0])) }) > 0;
    const periodLabel = `${filters.rangeDays}D`;

    return (
        <div className="space-y-6 font-mono">
            <div className="border-b border-border pb-4">
                <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">relatórios / navegação</p>
                <h1 className="mt-1 text-3xl font-semibold tracking-tight text-foreground">Navegação</h1>
                <p className="mt-1 text-xs text-muted-foreground md:text-sm">
                    Por onde o lead entra (P1), para onde vai depois (P2), com que UTMs chegou e em que horários
                </p>
            </div>

            <Filters state={filtersForBar} projects={projects} isOwner={isOwner} domains={domains} busy={busy} />

            {!hasProject ? (
                <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border bg-card py-16 text-center">
                    <FolderKanban className="h-8 w-8 text-muted-foreground/60" />
                    <p className="text-sm font-medium text-foreground">Selecione ao menos um projeto para carregar o relatório</p>
                </div>
            ) : entryPath ? (
                <EntryDetail
                    entryPath={entryPath}
                    request={filters.request}
                    periodLabel={periodLabel}
                    onBack={() => setEntryPath(null)}
                    onOpenAsEntry={(path) => setEntryPath(path)}
                />
            ) : isError ? (
                <ErrorState message={(error as any)?.response?.data?.detail || (error as Error)?.message} onRetry={() => refetch()} />
            ) : isLoading || !data ? (
                <div className="flex items-center justify-center rounded-lg border border-border bg-card py-16 text-muted-foreground">
                    <div className="mr-3 h-4 w-4 animate-spin rounded-full border-b-2 border-muted-foreground" />
                    <span className="text-sm">Montando as páginas de entrada…</span>
                </div>
            ) : data.entries.length === 0 ? (
                <EmptyState title="Sem sessões no período" description="Nenhuma sessão registrada no recorte selecionado. Ajuste o período, os projetos ou os domínios." />
            ) : (
                <>
                    <EntriesCards totals={data.totals} periodLabel={periodLabel} />
                    <EntriesTable items={data.entries} onSelect={(path) => setEntryPath(path)} />
                    <p className="text-right text-[10px] text-muted-foreground">
                        {data.meta.domains.length} domínio(s) no escopo · até {data.meta.limit} entradas por consulta · clique numa linha para ver P2s, UTMs e horários
                    </p>
                </>
            )}
        </div>
    );
}
